import { Dialog } from '@material-ui/core';
import React from 'react';

interface Props {
  open: boolean;
  title: string;
  message?: string;
  hideCancel?: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}

const ConfirmationDialog = ({
  open,
  title,
  message,
  onConfirm,
  onDismiss,
  hideCancel,
}: Props) => {
  return (
    <Dialog open={open} onClose={onDismiss}>
      <div className="modal fade show" id="alertModal">
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content rounded">
            <div className="modal-body py-4">
              <div
                className="cursor-pointer"
                onClick={() => {
                  onDismiss();
                }}
              >
                <i className="icon-close position-absolute right-0 top-0 p-2 font-xxs"></i>
              </div>
              <div className="px-2">
                <h6 className="font-xs font-weight-semibold mb-3">{title}</h6>
                <div className="row gutter-5 justify-content-center">
                  <div className="col-auto">
                    <button
                      onClick={() => {
                        onConfirm();
                      }}
                      type="button"
                      className="btn btn-sm btn-primary font-xs font-weight-semibold px-4"
                    >
                      <span className="px-2">Ok</span>
                    </button>
                  </div>
                  {!hideCancel && (
                    <div className="col-auto">
                      <button
                        onClick={() => {
                          onDismiss();
                        }}
                        type="button"
                        className="btn btn-sm btn-outline-secondary font-xs font-weight-semibold px-4"
                      >
                        <span className="px-2">Cancel</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </Dialog>
  );
};

const ConfirmationDialogContext = React.createContext({});

const ConfirmationDialogProvider = ({ children }: any) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogConfig, setDialogConfig] = React.useState<any>({});

  const openDialog = ({ title, message, actionCallback, hideCancel }: any) => {
    setDialogOpen(true);
    setDialogConfig({ title, message, actionCallback, hideCancel });
  };

  const resetDialog = () => {
    setDialogOpen(false);
    setDialogConfig({});
  };

  const onConfirm = () => {
    resetDialog();
    dialogConfig.actionCallback(true);
  };

  const onDismiss = () => {
    resetDialog();
    dialogConfig.actionCallback(false);
  };

  return (
    <ConfirmationDialogContext.Provider value={{ openDialog }}>
      <ConfirmationDialog
        open={dialogOpen}
        title={dialogConfig?.title}
        message={dialogConfig?.message}
        hideCancel={dialogConfig?.hideCancel}
        onConfirm={onConfirm}
        onDismiss={onDismiss}
      />
      {children}
    </ConfirmationDialogContext.Provider>
  );
};

const useConfirmationDialog = () => {
  const { openDialog }: any = React.useContext(ConfirmationDialogContext);

  const getConfirmation = ({
    ...options
  }: {
    title: string;
    message?: string;
    hideCancel?: boolean;
  }) =>
    new Promise((res) => {
      openDialog({ actionCallback: res, ...options });
    });

  return { getConfirmation };
};

export default ConfirmationDialog;
export { ConfirmationDialogProvider, useConfirmationDialog };
